import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, Search, Loader2, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { StudentEnrolmentPanel } from '@/components/enrollment/StudentEnrolmentPanel';
import { BulkEnrolDialog } from '@/components/enrollment/BulkEnrolDialog';

const API = process.env.REACT_APP_BACKEND_URL;
export const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`, 'Content-Type': 'application/json' });
export const enrollApi = async (path, opts = {}) => {
  const res = await fetch(`${API}/api/enrollment${path}`, { headers: authHeaders(), ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
  return data;
};

export default function StudentEnrollmentPage() {
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState('');
  const [year, setYear] = useState('');
  const [q, setQ] = useState('');
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => {
    enrollApi('/schools').then((s) => { setSchools(s); setSchoolId((cur) => cur || (s[0]?.id ?? '')); }).catch((e) => toast.error(e.message));
  }, []);

  const load = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ school_id: schoolId });
      if (q.trim()) params.set('q', q.trim());
      if (year) params.set('year', year);
      const [st, cl] = await Promise.all([enrollApi(`/students?${params}`), enrollApi(`/classes?school_id=${schoolId}`)]);
      setStudents(st);
      setClasses(cl);
      setSelected((cur) => (cur ? st.find((s) => s.id === cur.id) || null : null));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, q, year]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const years = useMemo(() => [...new Set(classes.map((c) => c.year))].sort((a, b) => a - b), [classes]);
  const school = schools.find((s) => s.id === schoolId);

  return (
    <div className="space-y-6" data-testid="student-enrollment-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><GraduationCap className="h-7 w-7 text-teal-700" /> Assign Students to Subjects</h1>
          <p className="text-muted-foreground mt-1">Pick a student, then tick the subject classes they belong to. Teachers see enrolled students in roll call and the gradebook.</p>
        </div>
        <Button variant="outline" onClick={() => setBulkOpen(true)} disabled={!schoolId} className="gap-2" data-testid="bulk-enrol-btn">
          <Users className="h-4 w-4" /> Enrol a whole year group
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <select value={schoolId} onChange={(e) => { setSchoolId(e.target.value); setSelected(null); setYear(''); }} className="h-10 border rounded-md px-3 bg-background text-sm" data-testid="enrol-school-select">
          {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={year} onChange={(e) => setYear(e.target.value)} className="h-10 border rounded-md px-3 bg-background text-sm" data-testid="enrol-year-select">
          <option value="">All years</option>
          {years.map((y) => <option key={y} value={y}>Year {y}</option>)}
        </select>
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search student name or ID…" className="pl-9" data-testid="enrol-student-search" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3"><CardTitle className="text-base">Students {loading ? <Loader2 className="inline h-4 w-4 animate-spin ml-1" /> : <span className="text-muted-foreground font-normal">({students.length})</span>}</CardTitle></CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y max-h-[65vh] overflow-auto" data-testid="enrol-student-list">
              {students.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => setSelected(s)}
                    className={`w-full text-left px-4 py-2.5 hover:bg-muted/50 transition-colors ${selected?.id === s.id ? 'bg-teal-50 border-l-4 border-teal-600' : 'border-l-4 border-transparent'}`}
                    data-testid={`enrol-student-${s.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">{s.name}</span>
                      <Badge variant="secondary" className="shrink-0">{s.classes.length} {s.classes.length === 1 ? 'subject' : 'subjects'}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{[s.year && `Year ${s.year}`, s.student_number && `ID ${s.student_number}`].filter(Boolean).join(' · ')}</p>
                  </button>
                </li>
              ))}
              {!loading && students.length === 0 && <li className="p-6 text-sm text-muted-foreground text-center">No students found.</li>}
            </ul>
          </CardContent>
        </Card>

        <div className="lg:col-span-3">
          <StudentEnrolmentPanel student={selected} classes={classes} onChanged={load} />
        </div>
      </div>

      <BulkEnrolDialog open={bulkOpen} onOpenChange={setBulkOpen} schoolId={schoolId} schoolName={school?.name} classes={classes} onDone={load} />
    </div>
  );
}
